import { zodResolver } from '@hookform/resolvers/zod'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { useEmpreendimentosDisponiveisSuspense } from '@/features/empreendimentos/hooks/use-empreendimentos-disponiveis-suspense'
import { Icon } from '@/shared/components/icon'
import { Button, InputField, Skeleton } from '@/shared/components/ui'
import { ComboboxField } from '@/shared/components/ui/combobox'
import { useSearchParamsWithTimestamp } from '@/shared/hooks'

const ConsultaLeadsRepresadosFiltrosBuscaSchema = z.object({
  idExternoEmpreendimento: z.number().nullish(),
  nomeCliente: z.string().nullish(),
  telefoneCliente: z.string().nullish(),
})

type TConsultaLeadsRepresadosFiltrosBuscaInput = z.input<typeof ConsultaLeadsRepresadosFiltrosBuscaSchema>
type TConsultaLeadsRepresadosFiltrosBuscaOutput = z.output<typeof ConsultaLeadsRepresadosFiltrosBuscaSchema>

export const LeadsRepresadosFiltros = () => {
  const { t } = useTranslation('leads-represados')
  const { filters, setFilters } = useSearchParamsWithTimestamp<TConsultaLeadsRepresadosFiltrosBuscaOutput>()
  const { data: empreendimentos } = useEmpreendimentosDisponiveisSuspense()

  const form = useForm<TConsultaLeadsRepresadosFiltrosBuscaInput, unknown, TConsultaLeadsRepresadosFiltrosBuscaOutput>({
    mode: 'onSubmit',
    defaultValues: {
      idExternoEmpreendimento: filters.idExternoEmpreendimento ? Number(filters.idExternoEmpreendimento) : null,
      nomeCliente: filters.nomeCliente ?? '',
      telefoneCliente: filters.telefoneCliente ?? '',
    },
    resolver: zodResolver(ConsultaLeadsRepresadosFiltrosBuscaSchema),
  })

  const handleFiltrosNaUrl = (data: TConsultaLeadsRepresadosFiltrosBuscaOutput) => {
    setFilters(data, { withTimestamp: true })
  }
  const onClear = () => {
    form.reset({ idExternoEmpreendimento: null, nomeCliente: '', telefoneCliente: '' })
  }

  return (
    <div className="br-12 bg-foreground border-muted mx-auto w-full space-y-8 rounded-sm rounded-b-none border-b-1 px-6 shadow-xs sm:max-w-4xl">
      <h2 className="text-neutral-800">{t('pages.consulta.subtitulo')}</h2>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(handleFiltrosNaUrl)}>
          <div className="md:grid md:grid-cols-1 md:gap-0 lg:grid-cols-3 lg:gap-4">
            <ComboboxField
              control={form.control}
              name="idExternoEmpreendimento"
              label={t('pages.consulta.filtros.empreendimento')}
              placeholder={t('pages.consulta.filtros.placeholder.empreendimento')}
              options={empreendimentos}
              valueProperty="idExterno"
              labelProperty="nome"
            />
            <InputField
              id="nome"
              name="nomeCliente"
              control={form.control}
              type="text"
              label={t('pages.consulta.filtros.nome')}
              placeholder={t('pages.consulta.filtros.placeholder.nome')}
            />
            <InputField
              id="telefone"
              name="telefoneCliente"
              control={form.control}
              type="number"
              label={t('pages.consulta.filtros.telefone')}
              placeholder={t('pages.consulta.filtros.placeholder.telefone')}
            />
          </div>
          <div className="col-span-full mb-6 flex w-full flex-col-reverse justify-end gap-4 md:flex-row">
            <Button variant="outline" type="button" className="w-full md:w-37" onClick={onClear}>
              <Icon name="trash-2" />
              {t('pages.consulta.filtros.limpar_filtro')}
            </Button>
            <Button type="submit" className="w-full md:w-37">
              <Icon name="search" />
              {t('pages.consulta.filtros.buscar_cliente')}
            </Button>
          </div>
        </form>
      </FormProvider>
    </div>
  )
}

export const LeadsRepresadosFiltrosSkeleton = () => {
  return (
    <div className="br-12 bg-foreground border-muted mx-auto w-full space-y-8 rounded-sm rounded-b-none border-b-1 px-6 shadow-xs sm:max-w-4xl">
      <Skeleton className="h-6 w-48" />
      <div className="md:grid md:grid-cols-1 md:gap-0 lg:grid-cols-3 lg:gap-4">
        <div className="mb-4 space-y-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-10 w-full bg-gray-50" />
        </div>
        <div className="mb-4 space-y-2">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-10 w-full bg-gray-50" />
        </div>
        <div className="mb-4 space-y-2">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-10 w-full bg-gray-50" />
        </div>
      </div>
      <div className="col-span-full mb-6 flex w-full justify-end gap-4">
        <Skeleton className="h-10 w-37" />
        <Skeleton className="h-10 w-37" />
      </div>
    </div>
  )
}
